import {useSessionEvents} from '../../hooks/use-session-events'
import {useListCommands} from '../../hooks/useListCommands'
import {useAgentProfile} from '../../contexts/agent-profile-context'
import {useOptimisticLogsOptional} from '../../contexts/OptimisticLogsContext'
import {useSessionContext} from '../../contexts/SessionContext'
import {TERMINAL_SESSION_STATES} from '../../types/session'
import {isTurn} from '../../types/events'
import {LoadingMessage} from './LoadingMessage'
import {PendingPromptNotice} from './PendingPromptNotice'
import {SessionLogsBlankSlate, type SessionLogsEmptyReason} from './SessionLogsBlankSlate'
import {TurnView} from './events/TurnView'
import {Abort} from './events/Abort'
import styles from './EventSessionLogs.module.css'
import {useSessionLoadingSource} from '../../hooks/use-session-loading-presentation'
import {isConversationRefused} from '../../utils/conversation-access'
import {useReportConversationRefused} from '../../contexts/ConversationAccessContext'
import {ConversationLocked} from '../conversation-access/ConversationLocked'

export interface EventSessionLogsProps {
  /** The task ID to fetch events for */
  taskId: string | undefined
  /** Polling interval in ms (fallback when Alive is not connected) */
  pollingInterval?: number
  /** Which blank-slate copy to show when there are no events to render. */
  emptyReason?: SessionLogsEmptyReason
  loadingPresentation?: 'local' | 'page'
}

/**
 * Session logs component powered by the events API.
 * Fetches raw SessionEvents via Alive push + cursor-based incremental loading,
 * builds a ConversationModel, and renders the timeline.
 */
export function EventSessionLogs({
  taskId,
  pollingInterval = 3000,
  emptyReason = 'no-events',
  loadingPresentation = 'local',
}: EventSessionLogsProps) {
  const {data: commands} = useListCommands({pollingInterval})
  const {data, isLoading, error, refetch} = useSessionEvents({taskId, pollingInterval, enabled: true, commands})
  const refused = isConversationRefused(error)
  useReportConversationRefused(taskId, refused)
  const agentProfile = useAgentProfile()
  const optimisticLogs = useOptimisticLogsOptional()
  const {session} = useSessionContext()
  const model = data?.model
  useSessionLoadingSource(
    (error && !model) || refused
      ? {kind: 'history', state: 'error', replacesLoading: false}
      : {kind: 'history', state: isLoading && !model ? 'pending' : 'idle'},
    {enabled: loadingPresentation === 'page'},
  )

  const EmptyStateComponent = agentProfile.slots.EmptyState
  const isEmptySession = !!model && !model.hasVisibleContent && !model.isWorking && !!EmptyStateComponent

  // Show thinking indicator when the agent is working OR when the user has
  // responded to a prompt but the server hasn't confirmed yet (optimistic gap).
  const hasUnreconciledOptimistic = (optimisticLogs?.optimisticResponses.length ?? 0) > 0
  const showThinking = model && (model.isWorking || hasUnreconciledOptimistic)

  // Show prompt notice when the session is not steerable, has a pending prompt, and is not terminal
  const isTerminalSession = TERMINAL_SESSION_STATES.has(session.state)
  const showPendingPromptNotice = !!model?.pendingPrompt && session.remote_steerable === false && !isTerminalSession

  if (refused) {
    return <ConversationLocked surface="task" onRetry={() => void refetch()} />
  }

  if (isLoading && !model) {
    return loadingPresentation === 'local' ? <LoadingMessage message="Loading…" /> : null
  }

  if (error && !model) {
    return <div>Error loading session events.</div>
  }

  if (!model) {
    return <SessionLogsBlankSlate reason={emptyReason} />
  }

  if (isEmptySession && EmptyStateComponent) {
    return <EmptyStateComponent />
  }

  return (
    <div data-testid="event-session-logs" className={styles.container}>
      {!model.hasVisibleContent && !model.isWorking && <SessionLogsBlankSlate reason={emptyReason} />}
      {model.items.map((item, index) => {
        if (isTurn(item)) {
          return (
            <TurnView
              key={item.turnId ?? `turn-${index}`}
              turn={item}
              mcpToolNameMappings={model.mcpToolNameMappings}
            />
          )
        }

        return <Abort key={item.id} item={item} />
      })}
      {showThinking && <LoadingMessage message={model.currentIntent || 'Thinking…'} alignWithTools />}
      {showPendingPromptNotice && <PendingPromptNotice />}
    </div>
  )
}
